import { isSuperAdmin } from "@/helpers/UserHelpers";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@/services/slices/authSlice";
import IconButton from "@/components/atoms/IconButton";
import Modal from "@/components/atoms/Modal";
import Select, { SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";
import { EMAIL_REGEX, PASSWORD_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL2_MESSAGE,
  INVALID_EMAIL_MESSAGE,
  INVALID_FIRSTNAME_MESSAGE,
  INVALID_FORM_MESSAGE,
  INVALID_LASTNAME_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_PASSWORD2_MESSAGE,
  INVALID_PASSWORD_MESSAGE,
  INVALID_PHONE2_MESSAGE,
} from "@/constants/validatorMessages";
import { USER_ROLES, UserForm, UserRoleType } from "@/interfaces/IUser";
import { IModalMethods } from "@/interfaces/Modal";
import { useStoreUserMutation } from "@/services/users";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StoreUserModal: ForwardRefRenderFunction<IModalMethods> = (_, ref) => {
  const currentUser = useSelector(selectCurrentUser);
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  // Actif d'office : le login refuse les comptes inactifs, et l'admin qui
  // choisit le mot de passe crée un compte prêt à servir.
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<UserForm>({ defaultValues: { isActive: true } });
  const [storeUserAsync] = useStoreUserMutation();
  const { loadOrganisationsOptions } = useLoadOptions();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<UserForm> = ({
    roles,
    phone,
    organisation,
    ...data
  }) => {
    toast.promise(
      storeUserAsync({
        ...data,
        phone: phone ? parsePhoneNumber(phone, "FR")?.number : undefined,
        roles: roles.map((role) => role.value as UserRoleType),
        organisationId: organisation?.id ?? null,
      }).unwrap(),
      {
        pending: "Ajout de l'utilisateur en cours...",
        success: {
          render() {
            reset();
            showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L'utilisateur à bien été ajouté 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<UserForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter un utilisateur"
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Prenom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Ilan"
              type="text"
              {...register("firstname", {
                required: INVALID_FIRSTNAME_MESSAGE,
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
                maxLength: {
                  value: 255,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.firstname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstname?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom de famille <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : JOURNO"
              type="text"
              {...register("lastname", {
                required: INVALID_LASTNAME_MESSAGE,
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
                maxLength: {
                  value: 255,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.lastname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastname?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Email <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : ilan@groupe-pi.fr"
              type="email"
              {...register("email", {
                required: INVALID_EMAIL_MESSAGE,
                pattern: {
                  value: EMAIL_REGEX,
                  message: INVALID_EMAIL2_MESSAGE,
                },
              })}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3" controlId="store-user-password">
            <Form.Label>
              Mot de passe <span className="text-danger">*</span> :
            </Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                placeholder="ex : 98Koilju@1"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                {...register("password", {
                  required: INVALID_PASSWORD_MESSAGE,
                  pattern: {
                    value: PASSWORD_REGEX,
                    message: INVALID_PASSWORD2_MESSAGE,
                  },
                })}
                isInvalid={!!errors.password}
              />
              <InputGroup.Text
                as={IconButton}
                variant="outline-secondary"
                icon={showPassword ? faEyeSlash : faEye}
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle-button"
                aria-label={
                  showPassword
                    ? "Masquer le mot de passe"
                    : "Afficher le mot de passe"
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors.password?.message}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>

        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Numéro de téléphone :</Form.Label>
            <Form.Control
              placeholder="ex : 0634835532"
              type="tel"
              {...register("phone", {
                validate: (value) => {
                  if (value && !isValidPhoneNumber(value, "FR")) {
                    return INVALID_PHONE2_MESSAGE;
                  }
                },
              })}
              isInvalid={!!errors.phone}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phone?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Commission du Head Of Sales :</Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                {...register("headOfSalesCommission")}
                placeholder="1"
                step={0.1}
                isInvalid={!!errors.headOfSalesCommission}
              />
              <InputGroup.Text>%</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.headOfSalesCommission?.message}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>Signature électronique :</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              {...register("signatureFile")}
              isInvalid={!!errors.signatureFile}
            />
            <Form.Control.Feedback type="invalid">
              {errors.signatureFile?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Role(s) <span className="text-danger">*</span> :
            </Form.Label>
            <Controller
              control={control}
              name="roles"
              rules={{
                required: "Le rôle ou les rôles sont requis.",
              }}
              render={({ field }) => (
                <Select
                  options={USER_ROLES.filter(
                    (role) =>
                      role.value !== "SUPERADMIN" || isSuperAdmin(currentUser),
                  )}
                  value={field.value}
                  isMulti
                  onChange={field.onChange}
                />
              )}
            />
            {!!errors.roles ? (
              <h6 className="mb-0 mt-0 mt-1 text-danger">
                {errors.roles?.message}
              </h6>
            ) : null}
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>Organisme :</Form.Label>
            <Controller
              control={control}
              name="organisation"
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadOrganisationsOptions}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id.toString()}
                  value={field.value}
                  isClearable
                  onChange={field.onChange}
                />
              )}
            />
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3 me-auto">
            <Form.Label>Passer le compte actif :</Form.Label>
            <Form.Check
              type="switch"
              {...register("isActive")}
              isInvalid={!!errors.isActive}
            />
            <Form.Control.Feedback type="invalid">
              {errors.isActive?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3 me-auto">
            <Form.Label>Est un ancien :</Form.Label>
            <Form.Check
              type="switch"
              {...register("isVeteran")}
              isInvalid={!!errors.isVeteran}
            />
            <Form.Control.Feedback type="invalid">
              {errors.isVeteran?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreUserModal);
